import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useOverlayContext, ConfirmationModal } from 'seagull';
import { ErrorText } from '~/constants';
import { useStoreActions } from '~/contexts';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
import { AgreementTerminationModal } from './AgreementTerminationModal';
const sharedModalConfig = {
    closeOnClickOutside: true,
    closeOnEscapeKey: true,
    shouldBlurBackground: true
};
export function useTerminateAgreement() {
    const { api, logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    async function doActionWithToasts(action, successMessage, errorMessage) {
        try {
            await action();
            overlayContext.showSuccessToast(successMessage, 'termination-success-toast');
        } catch (err) {
            overlayContext.showErrorToast(errorMessage(err), 'termination-failure-toast');
        }
    }
    async function simpleApproveTermination(config) {
        const { modalProps, successMessage, errorMessage, action, biCallerContext, biName } = config;
        const confirmed = await overlayContext.showModal(_object_spread_props(_object_spread({}, sharedModalConfig), {
            biCallerContext: biCallerContext !== null && biCallerContext !== void 0 ? biCallerContext : undefined,
            biName,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({}, modalProps), {
                    skin: "danger",
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false)
                }));
            }
        }));
        if (confirmed) {
            await doActionWithToasts(action, successMessage, errorMessage);
        }
    }
    return {
        terminateAgreementById: async (config)=>{
            const { id, postAction, biCallerContext } = config;
            const result = await overlayContext.showModal(_object_spread_props(_object_spread({
                biName: 'agreement_termination_modal'
            }, sharedModalConfig), {
                biCallerContext,
                content: (p)=>/*#__PURE__*/ _jsx(AgreementTerminationModal, _object_spread({
                        relationshipId: id
                    }, p))
            }));
            const { confirmed, notification } = result || {};
            if (confirmed) {
                await doActionWithToasts(async ()=>{
                    logBI(10573, {});
                    await api.relationship.terminateAgreement(id, notification || null);
                    await postAction();
                }, 'Agreement was terminated successfully', (e)=>new ErrorResponse(e).hasServerError(ServerError.RelationshipAlreadyTerminated) ? 'Agreement already terminated' : 'Failed to terminate agreement');
            }
        },
        terminateDraftById: (id, postAction, biCallerContext)=>simpleApproveTermination({
                action: async ()=>{
                    await api.draft.deleteById(id);
                    await postAction();
                },
                successMessage: 'Draft was discarded successfully',
                errorMessage: ()=>'Failed to discard draft',
                biName: 'terminate_draft_modal',
                biCallerContext,
                modalProps: {
                    bi: {
                        primaryButton: {
                            name: 'terminate_draft_modal_primary_button'
                        },
                        secondaryButton: {
                            name: 'terminate_draft_modal_secondary_button'
                        }
                    },
                    title: 'Discard this draft?',
                    content: "If you discard this draft it will be removed from the system and you won't be able to access it",
                    primaryButtonLabel: 'Discard draft',
                    secondaryButtonLabel: 'Keep draft'
                }
            }),
        cancelProposalById: (id, postAction)=>simpleApproveTermination({
                action: async ()=>{
                    const loaderName = 'cancelProposal';
                    overlayContext.startLoader(loaderName);
                    try {
                        await api.relationship.cancelProposal(id);
                        await postAction();
                    } finally{
                        overlayContext.stopLoader(loaderName);
                    }
                },
                successMessage: 'Proposal was canceled successfully',
                errorMessage: (err)=>new ErrorResponse(err).hasServerError(ServerError.InvalidRelationshipStatus) ? ErrorText.ProposalAlreadyAcceptedOrCanceled : 'Failed to cancel proposal',
                biName: 'cancel_proposal_modal',
                modalProps: {
                    bi: {
                        primaryButton: {
                            name: 'cancel_proposal_primary_button'
                        },
                        secondaryButton: {
                            name: 'cancel_proposal_secondary_button'
                        }
                    },
                    title: 'Cancel this proposal?',
                    content: 'If you cancel this proposal it will be removed from the system and neither you or your client will have access to it.',
                    primaryButtonLabel: 'Cancel proposal',
                    secondaryButtonLabel: 'Keep proposal'
                }
            })
    };
}
